// Global Search Component
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// All searchable features/pages
const SEARCH_ITEMS = [
  // Tankar & Känslor
  { title: 'Dagbok', path: '/journal', category: 'Tankar & Känslor', keywords: ['skriva', 'anteckningar', 'tankar'] },
  { title: 'Humörloggning', path: '/mood', category: 'Tankar & Känslor', keywords: ['känslor', 'mående', 'humör'] },
  { title: 'Tacksamhet', path: '/gratitude', category: 'Tankar & Känslor', keywords: ['tacksam', 'positivt'] },
  { title: 'Reflektioner', path: '/reflections', category: 'Tankar & Känslor', keywords: ['tänka', 'fundera'] },
  { title: 'Självkännedom', path: '/self-discovery', category: 'Tankar & Känslor', keywords: ['quiz', 'personlighet'] },
  { title: 'Känslor', path: '/emotions', category: 'Tankar & Känslor', keywords: ['emotion', 'känsla'] },
  
  // Hälsa
  { title: 'Träning', path: '/fitness', category: 'Hälsa', keywords: ['gym', 'övningar', 'workout', 'program'] },
  { title: 'Kost', path: '/nutrition', category: 'Hälsa', keywords: ['mat', 'recept', 'näring', 'måltid'] },
  { title: 'Sömn', path: '/sleep', category: 'Hälsa', keywords: ['vila', 'sova', 'natt'] },
  { title: 'Vatten', path: '/water', category: 'Hälsa', keywords: ['dricka', 'glas'] },
  { title: 'Steg', path: '/steps', category: 'Hälsa', keywords: ['gå', 'promenad', 'räkna'] },
  { title: 'Hormoner', path: '/hormones', category: 'Hälsa', keywords: ['mens', 'cykel', 'pms'] },
  { title: 'Hälsologg', path: '/health-log', category: 'Hälsa', keywords: ['symptom', 'medicin'] },
  
  // Lugn
  { title: 'Andning', path: '/exercises', category: 'Lugn', keywords: ['andas', 'lugna', 'stress'] },
  { title: 'Meditation', path: '/meditation', category: 'Lugn', keywords: ['mindfulness', 'avslappning'] },
  { title: 'Fokus', path: '/focus', category: 'Lugn', keywords: ['koncentration', 'pomodoro'] },
  { title: 'Minne', path: '/memory', category: 'Lugn', keywords: ['träna', 'hjärna'] },
  
  // Hem
  { title: 'Städning', path: '/cleaning', category: 'Hem', keywords: ['rengöring', 'hemma', 'hushåll'] },
  { title: 'Inköp', path: '/shopping', category: 'Hem', keywords: ['lista', 'handla'] },
  { title: 'Rutiner', path: '/routines', category: 'Hem', keywords: ['morgon', 'kväll', 'schema'] },
  
  // Planering
  { title: 'Mål', path: '/goals', category: 'Planering', keywords: ['target', 'uppnå'] },
  { title: 'Vanor', path: '/habits', category: 'Planering', keywords: ['habit', 'vana', 'spåra'] },
  { title: 'Att-göra', path: '/todos', category: 'Planering', keywords: ['todo', 'uppgift', 'lista'] },
  { title: 'Veckoplanerare', path: '/week-planner', category: 'Planering', keywords: ['schema', 'kalender'] },
  { title: 'Påminnelser', path: '/reminders', category: 'Planering', keywords: ['alarm', 'notis'] },
  { title: 'Viktiga datum', path: '/dates', category: 'Planering', keywords: ['födelsedag', 'årsdag'] },
  
  // Ekonomi
  { title: 'Budget', path: '/finance', category: 'Ekonomi', keywords: ['pengar', 'utgifter'] },
  { title: 'Sparande', path: '/savings', category: 'Ekonomi', keywords: ['spara', 'mål'] },
  { title: 'Skulder', path: '/debts', category: 'Ekonomi', keywords: ['lån', 'betala'] },
  { title: 'Karriär', path: '/career', category: 'Ekonomi', keywords: ['jobb', 'arbete', 'cv'] },
  
  // Relationer
  { title: 'Kommunikation', path: '/relationships', category: 'Relationer', keywords: ['prata', 'lyssna'] },
  { title: 'Gränser', path: '/boundaries', category: 'Relationer', keywords: ['säga nej', 'gräns'] },
  { title: 'Brev', path: '/letters', category: 'Relationer', keywords: ['skriva', 'meddelande'] },
  { title: 'Parövningar', path: '/couple-exercises', category: 'Relationer', keywords: ['relation', 'partner'] },
  
  // Övrigt
  { title: 'Inställningar', path: '/settings', category: 'Övrigt', keywords: ['konto', 'profil', 'lösenord'] },
  { title: 'Prenumeration', path: '/subscription', category: 'Övrigt', keywords: ['betala', 'premium'] },
  { title: 'Statistik', path: '/stats', category: 'Övrigt', keywords: ['data', 'översikt'] },
  { title: 'Träningshistorik', path: '/workout-history', category: 'Övrigt', keywords: ['logg', 'pass'] },
];

export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchQuery = query.toLowerCase();
    const filtered = SEARCH_ITEMS.filter(item => {
      return (
        item.title.toLowerCase().includes(searchQuery) ||
        item.category.toLowerCase().includes(searchQuery) ||
        item.keywords.some(k => k.includes(searchQuery))
      );
    }).slice(0, 8);

    setResults(filtered);
  }, [query]);

  const handleSelect = (path) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="max-w-lg mx-auto mt-20 mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-background rounded-2xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Sök funktioner..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto">
            {results.length > 0 ? (
              <ul className="p-2">
                {results.map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleSelect(item.path)}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                      <div>
                        <p className="font-medium text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.category}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : query.trim() ? (
              <div className="p-8 text-center text-muted-foreground">
                Inga resultat för "{query}"
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                Börja skriva för att söka...
              </div>
            )}
          </div>

          {/* Keyboard hint */}
          <div className="p-3 border-t bg-muted/30 text-center">
            <span className="text-xs text-muted-foreground">
              Tryck <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">ESC</kbd> för att stänga
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
